import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useApiData } from "../hooks/useApiData";
import "./PageContent.css";
import "./NoticeListPage.css";

const ENDPOINT_META = {
  notices: { title: "Notices", icon: "fa-bullhorn" },
  advertisements: { title: "Advertisements", icon: "fa-bullhorn" },
  forms: { title: "Forms", icon: "fa-file-text-o" },
  results: { title: "Exam Results", icon: "fa-graduation-cap" },
  admission: { title: "Admission", icon: "fa-user-plus" },
  tenders: { title: "Tenders", icon: "fa-gavel" },
  mci: { title: "MCI", icon: "fa-hospital-o" },
};

const INITIAL_VISIBLE = 10;
const LOAD_MORE_STEP = 10;

export default function NoticeListPage() {
  const { endpoint } = useParams();
  const { data: items, loading } = useApiData(endpoint);
  const { data: leaders } = useApiData("leadership");

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  // Reset back to the first 20 whenever the user switches to a different
  // notice board (e.g. tenders -> results) so counts don't carry over.
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [endpoint]);

  const meta = ENDPOINT_META[endpoint] || {
    title: "Notice Board",
    icon: "fa-file-text-o",
  };

  const principal = leaders?.find((l) =>
    String(l.role || "").toLowerCase().match(/principal|dean/)
  );
  const medicalSuperintendent = leaders?.find((l) =>
    String(l.role || "").toLowerCase().includes("medical superintendent")
  );

  const slugify = (str) =>
    String(str || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const formatDate = (value) => {
    if (!value) return "";

    const parsed = new Date(value);

    if (isNaN(parsed.getTime())) {
      // Already a plain string like "20-08-2026" — show as-is
      return value;
    }

    const dd = String(parsed.getDate()).padStart(2, "0");
    const mm = String(parsed.getMonth() + 1).padStart(2, "0");
    const yyyy = parsed.getFullYear();

    return `${dd}-${mm}-${yyyy}`;
  };

  const renderOfficialMessage = (leader, title) => {
    if (!leader) {
      return null;
    }

    return (
      <div className="official-message-block">
        <div className="official-message-title">{title}</div>

        <div className="official-message-body">
          <div className="official-message-top">
            <div className="official-photo-wrapper">
              <img
                src={leader.img}
                alt={leader.name}
                className="official-photo"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    leader.name || "Official"
                  )}&background=0b5d7a&color=fff&size=150`;
                }}
              />
            </div>

            <div className="official-message-text">
              <p>{leader.message}</p>
            </div>
          </div>

          <div className="official-name">{leader.name}</div>

          <div className="official-role">{leader.role}</div>

          <div className="official-more-wrapper">
            <Link
              to={`/leadership-message/${slugify(leader.role)}`}
              className="official-more"
            >
              More...
            </Link>
          </div>
        </div>
      </div>
    );
  };

  const renderNoticeCard = (item) => {
    return (
      <div key={item.id} className="notice-card">
        <h3 className="notice-card-title">
          {item.title}
          {item.isNew ? <span className="badge-new">NEW</span> : null}
        </h3>

        {/* {item.description ? (
          <p className="notice-card-desc">{item.description}</p>
        ) : null} */}
        {item.details ? (
          <p className="notice-card-desc">{item.details}</p>
        ) : null}

        <div className="notice-card-footer">
          <span className="notice-card-date">
            <span className="notice-card-date-label">Published on : </span>
            {formatDate(item.date)}
          </span>

          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="notice-card-readmore"
          >
            Read More
          </a>
        </div>
      </div>
    );
  };

  const totalCount = items?.length || 0;
  const visibleItems = items ? items.slice(0, visibleCount) : [];
  const hasMore = totalCount > visibleCount;

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + LOAD_MORE_STEP);
  };

  return (
    <main className="content-page-wrapper">
      <div className="content-breadcrumb-wrapper">
        <div className="content-container">
          <div className="content-breadcrumb">
            <Link to="/">
              <i className="fa fa-home"></i>
              Home
            </Link>

            <span className="breadcrumb-arrow">
              <i className="fa fa-angle-right"></i>
            </span>

            <span className="breadcrumb-current">{meta.title}</span>
          </div>
        </div>
      </div>

      <div className="content-container">
        <div className="content-layout">
          <aside className="content-sidebar">
            <div className="official-info-card">
              <div className="official-info-header">
                <div className="official-info-icon">
                  <i className="fa fa-envelope-o"></i>
                </div>
                <span>MESSAGE FROM</span>
              </div>

              <div className="official-info-body">
                {renderOfficialMessage(principal, "DEAN AND PRINCIPAL")}
                {renderOfficialMessage(
                  medicalSuperintendent,
                  "MEDICAL SUPERINTENDENT"
                )}
              </div>
            </div>
          </aside>

          <section className="content-main-card">
            <h1 className="notice-page-title">MKCG Medical College {meta.title}</h1>

            {loading ? (
              <div className="content-loading">
                <div className="content-spinner"></div>
                <p>Loading {meta.title.toLowerCase()}...</p>
              </div>
            ) : totalCount > 0 ? (
              <>
                <div className="notice-card-list">
                  {visibleItems.map((item) => renderNoticeCard(item))}
                </div>

                {hasMore && (
                  <div className="notice-load-more-wrap">
                    <button
                      type="button"
                      className="notice-load-more-btn"
                      onClick={handleLoadMore}
                    >
                      Load More
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className="notice-card-empty">
                No {meta.title.toLowerCase()} found.
              </p>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
